import { Access } from "@p0security/shared/types/access/index";

// The parameters every Argo CD access shares.
const instanceParameters = {
  // An installed instance, by its id or its label in P0. Optional on the
  // command line, which picks the only one installed. The request forms wait
  // for it, since the roles and projects they list are the instance's, and
  // select it when only one is installed (`newArgoCdCommands`).
  instance: {
    type: "static",
    label: "Argo CD instance",
    description:
      "The installed Argo CD instance, needed only when more than one is installed",
    optional: true,
    waitForInput: true,
  },
  // The id of the item `instance` resolved to.
  instanceId: { type: "generated", label: "Argo CD instance ID" },
} as const;

// The grantee's SSO email, which the grant's policy line names. Argo CD
// matches it as its IdP sends it, which has to be lowercase (an accepted
// limit), so P0 lowercases it too. Request-time checks see only the
// permission, so it's a parameter, not generated data.
const emailParameter = {
  email: {
    type: "generated",
    label: "Email",
    description: "The grantee's SSO email, lowercased",
  },
} as const;

/** Argo CD's access types. Each grant is one `g` line for the grantee's email
 * in the P0-owned `policy.p0.csv` key of `argocd-rbac-cm`, and its revoke
 * removes that line, so no access records the user's prior state.
 *
 * Shaped after Harbor's accesses
 * (packages/integrations/harbor/shared/src/accesses.ts); `global-role`, which
 * has no resource, renders as aws-midc's `permission-set` does.
 */
export const ArgocdAccesses = {
  "global-role": Access({
    description: "An Argo CD role on the whole instance",
    label: "Global role",
    maturity: "beta",
    delegation: {},
    generated: {},
    // Required parameters are CLI positionals, in this order, so this reads
    // `global-role <role> [--instance <id>]`.
    parameters: {
      ...instanceParameters,
      // Any role the instance's policy names, such as `role:readonly`, listed
      // from the instance. Static: an instance has tens of roles, so the
      // forms show them all as a dropdown, not a search box.
      role: {
        type: "static",
        label: "Role",
        description: "Argo CD role to grant",
      },
      ...emailParameter,
    },
    // Names the instance, so a notification says which Argo CD the role is
    // on.
    render: { locator: "instanceId", policy: "role" } as const,
  }),
  // Rests on an undocumented Argo CD behaviour: that a global
  // `g, <email>, proj:<project>:<role>` line grants the project's role (spec
  // assumption 5). If live testing disproves it, this access is removed.
  "project-role": Access({
    description: "A role already defined on an Argo CD project",
    label: "Project role",
    maturity: "beta",
    delegation: {},
    generated: {},
    // `project-role <project> <role> [--instance <id>]`.
    parameters: {
      ...instanceParameters,
      // Static, as `global-role`'s role is. The forms wait for it, since the
      // roles they list are the project's.
      project: {
        type: "static",
        label: "Project",
        description: "Argo CD project (AppProject) name",
        waitForInput: true,
      },
      // One of the project's own roles (`spec.roles`), listed for the
      // project chosen. P0 never creates or edits one.
      role: {
        type: "static",
        label: "Role",
        description: "Project role to grant",
      },
      ...emailParameter,
    },
    // Names the instance, so a notification says which Argo CD the project is
    // on.
    render: {
      locator: "instanceId",
      policy: "role",
      resource: "project",
    } as const,
  }),
};

export type ArgocdAccesses = typeof ArgocdAccesses;
export type ArgocdGlobalRoleAccess = ArgocdAccesses["global-role"];
export type ArgocdProjectRoleAccess = ArgocdAccesses["project-role"];
